import { IMAGE_CONTEXTS } from '@/constants/imageConfig';
import type { ImageContext } from '@/helpers/content/queries/types';

// Priority rules for different image contexts (works WITH Next.js)
export const getImagePriorityRules = (context: ImageContext) => {
  switch (context) {
    case IMAGE_CONTEXTS.BLOG_HERO:
      return {
        shouldPrioritize: true, // Hero images should always be prioritized
        description: 'Hero images are above the fold'
      };
    case IMAGE_CONTEXTS.BLOG_CARD:
      return {
        shouldPrioritize: true, // First few blog cards should be prioritized
        description: 'Blog cards are often above the fold'
      };
    case IMAGE_CONTEXTS.AUTHOR_AVATAR:
      return {
        shouldPrioritize: false, // Small avatars don't need priority
        description: 'Avatars are small and not critical for LCP'
      };
    case IMAGE_CONTEXTS.BLOG_CONTENT:
      return {
        shouldPrioritize: false, // Content images are below the fold
        description: 'Content images are typically below the fold'
      };
    case IMAGE_CONTEXTS.RELATED_POSTS:
      return {
        shouldPrioritize: false, // Related posts are at the bottom
        description: 'Related posts are at the bottom of the page'
      };
    default:
      return {
        shouldPrioritize: false,
        description: 'Default priority rule'
      };
  }
};

// Determine when to use blur placeholder (works WITH Next.js)
export const shouldUsePlaceholder = (
  originalWidth: number,
  originalHeight: number,
  context: ImageContext
): boolean => {
  // Use placeholder for larger images that benefit from blur effect
  const imageArea = originalWidth * originalHeight;
  const largeImageThreshold = 160000; // ~400x400 pixels
  
  switch (context) {
    case IMAGE_CONTEXTS.BLOG_HERO:
      return true; // Always use placeholder for hero images
    case IMAGE_CONTEXTS.BLOG_CARD:
      return imageArea > largeImageThreshold;
    case IMAGE_CONTEXTS.BLOG_CONTENT:
      return imageArea > largeImageThreshold;
    case IMAGE_CONTEXTS.AUTHOR_AVATAR:
      return false; // Small avatars don't need placeholders
    case IMAGE_CONTEXTS.RELATED_POSTS:
      return imageArea > largeImageThreshold;
    default:
      return imageArea > largeImageThreshold;
  }
};

// Get recommended Next.js Image props for different contexts
export const getRecommendedImageProps = (context: ImageContext) => {
  switch (context) {
    case IMAGE_CONTEXTS.BLOG_HERO:
      return {
        sizes: '(max-width: 768px) 100vw, 1200px',
        quality: 85,
        priority: true
      };
    case IMAGE_CONTEXTS.BLOG_CARD:
      return {
        sizes: '(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw',
        quality: 75,
        priority: true // Often above the fold
      };
    case IMAGE_CONTEXTS.BLOG_CONTENT:
      return {
        sizes: '(max-width: 768px) 100vw, (max-width: 1024px) 80vw, 800px',
        quality: 75,
        priority: false
      };
    case IMAGE_CONTEXTS.RELATED_POSTS:
      return {
        sizes: '(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 350px',
        quality: 70,
        priority: false
      };
    case IMAGE_CONTEXTS.AUTHOR_AVATAR:
      return {
        sizes: '100px',
        quality: 80,
        priority: false
      };
    default:
      return {
        sizes: '100vw',
        quality: 75,
        priority: false
      };
  }
};

// Helper function to get context-specific dimensions (for manual sizing if needed)
export const getContextDimensions = (context: ImageContext) => {
  switch (context) {
    case IMAGE_CONTEXTS.BLOG_HERO:
      return { width: 1200, height: 675 };
    case IMAGE_CONTEXTS.BLOG_CARD:
      return { width: 400, height: 225 };
    case IMAGE_CONTEXTS.BLOG_CONTENT:
      return { width: 800, height: 600 };
    case IMAGE_CONTEXTS.RELATED_POSTS:
      return { width: 350, height: 197 };
    case IMAGE_CONTEXTS.AUTHOR_AVATAR:
      return { width: 100, height: 100 };
    default:
      return { width: 800, height: 600 };
  }
}; 